import type { UserStateSavedSearchFolderKey, UserStateSavedSearchKey } from "./user-state";

export const EMAIL_ALERTS_ITEM_KEY = "emailAlerts" as const;
export const SAVED_SEARCH_ITEM_KEY_PREFIX = "savedSearch#" as const;
export const SAVED_SEARCH_FOLDER_ITEM_KEY_PREFIX = "savedSearchFolder#" as const;

/**
 * Identifier of the consent copy version shown at opt-in (spec §9). Bump when the copy
 * changes. v2: the copy no longer promises "no other emails, ever" — a signed-up user can
 * now separately opt into a second, independently-scheduled watchlist digest (see
 * WatchlistDigestAlertsData below); the toggle enabling it is its own consent act, but the
 * blanket "no other emails" claim in the base copy would otherwise be inaccurate for anyone
 * who turns it on.
 */
export const ALERTS_CONSENT_TEXT_VERSION = "alerts-consent-v2";

/** The digest cadences every user can pick (and the only ones the watchlist digest supports). */
export const ALERT_FREQUENCIES = ["weekly", "daily"] as const;
/** Plus only (TEC-744, spec 6.1): sent as soon as a matching job is indexed, batched per user. */
export const PLUS_ALERT_FREQUENCIES = ["instant"] as const;
export const ALL_ALERT_FREQUENCIES = [...ALERT_FREQUENCIES, ...PLUS_ALERT_FREQUENCIES] as const;
export type DigestAlertFrequency = (typeof ALERT_FREQUENCIES)[number];
export type AlertFrequency = (typeof ALL_ALERT_FREQUENCIES)[number];
export const DEFAULT_ALERT_FREQUENCY: AlertFrequency = "weekly";

export function isPlusAlertFrequency(value: unknown): value is (typeof PLUS_ALERT_FREQUENCIES)[number] {
  return typeof value === "string" && (PLUS_ALERT_FREQUENCIES as readonly string[]).includes(value);
}

export const EMAIL_ALERTS_STATUSES = ["active", "pendingConfirmation", "unsubscribed"] as const;
export type EmailAlertsStatus = (typeof EMAIL_ALERTS_STATUSES)[number];

export type AlertWithdrawalChannel = "profile" | "emailLink" | "oneClick" | "bounce" | "complaint";

/**
 * How a user's alerts reach them: email, notifications (the mobile app's push and browser web
 * push, on every device the user enabled them), or both. At least one is always on; turning both
 * off is unsubscribing.
 */
export interface AlertChannels {
  email: boolean;
  push: boolean;
}

/** Which of the two the user picked, as the settings UI offers it. */
export type AlertChannelChoice = "email" | "push" | "both";

export function alertChannelsFromChoice(choice: AlertChannelChoice): AlertChannels {
  return { email: choice !== "push", push: choice !== "email" };
}

export function alertChoiceFromChannels(channels: AlertChannels): AlertChannelChoice {
  return channels.email && channels.push ? "both" : channels.push ? "push" : "email";
}

/**
 * The channels a stored subscription uses. Records written before the choice existed have no
 * `channels`: they keep the behaviour they had, email plus app push when the app is installed.
 */
export function resolveAlertChannels(data: Pick<EmailAlertsData, "channels"> | null | undefined): AlertChannels {
  const c = data?.channels;
  if (!c || (!c.email && !c.push)) return { email: true, push: true };
  return { email: c.email === true, push: c.push === true };
}

/**
 * Double opt-in only guards email: a subscription that sends email to an unverified address waits
 * for confirmation; a notifications-only one is active straight away (the browser or the OS
 * already asked the user for permission).
 */
export function subscriptionStatusFor(channels: AlertChannels, emailVerified: boolean): EmailAlertsStatus {
  return channels.email && !emailVerified ? "pendingConfirmation" : "active";
}

export interface EmailAlertsData extends Record<string, unknown> {
  frequency: AlertFrequency;
  /** Captured from the session at opt-in; cleared on unsubscribe (spec §9). */
  email?: string;
  emailVerified: boolean;
  status: EmailAlertsStatus;
  locale: "en" | "fi" | "sv";
  /** Epoch ms of the last successful digest send; window lower bound. */
  lastSentAt: number | null;
  consentAt: number;
  consentTextVersion: string;
  consentSource: string;
  withdrawnAt?: number;
  withdrawnVia?: AlertWithdrawalChannel;
  /** Epoch ms the one allowed double-opt-in reminder was sent (TEC-514 #6). Absent while
   * status is pendingConfirmation and no reminder has gone out yet; cleared implicitly on a
   * fresh subscribe() call, which builds a new data object from scratch. */
  reminderSentAt?: number;
  /** Delivery channels; absent on records written before the choice existed (see resolveAlertChannels). */
  channels?: AlertChannels;
  /**
   * Instant alerts are a subscription-level cadence, capped by the tier's `instantAlerts` count
   * limit. Set by the job-match Lambda only while the user has MORE alert-enabled searches than
   * the cap allows: the oldest `instantSearchCap` stay instant and the rest ride the scheduled
   * daily run (lambda/alerts/instant-cap.ts). Absent = every alert-enabled search is instant.
   */
  instantSearchCap?: number;
}

/**
 * Browser web-push subscriptions, one per browser the user enabled notifications in (UserState item
 * `webPushSubscriptions`, written through PUT /api/user-state/webPushSubscriptions). The app's Expo
 * token stays its own `pushToken` item. The sender drops an entry when the push service answers 404
 * or 410 (the browser revoked it).
 */
export const WEB_PUSH_SUBSCRIPTIONS_ITEM_KEY = "webPushSubscriptions" as const;
/** Cap per user, oldest dropped first, so a user who clears site data often cannot grow the item without bound. */
export const WEB_PUSH_SUBSCRIPTIONS_MAX = 10;

export interface WebPushSubscriptionRecord {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  createdAt: number;
  /** Shown in settings ("Chrome on macOS") so a user can tell their browsers apart. */
  label?: string;
}

export interface WebPushSubscriptionsData extends Record<string, unknown> {
  subscriptions: WebPushSubscriptionRecord[];
}

export interface SavedSearchItemData extends Record<string, unknown> {
  /** Normalized saved-search URL (utils/saved-searches/normalize-saved-search-url.ts). */
  url: string;
  label?: string;
  alertEnabled: boolean;
  createdAt: number;
  /** Id of the savedSearchFolder# this search is organized into; absent = "Unsorted". */
  folderId?: string;
  /** Manual sort position within its folder group; only meaningful in "Custom order" sort mode. */
  order?: number;
}

export interface SavedSearchFolderData extends Record<string, unknown> {
  name: string;
  /** Manual sort position among the user's other folders. */
  order: number;
  createdAt: number;
}

/**
 * The Watchlist digest (new-from-followed-companies / watched-roles sections), split out of
 * the combined emailAlerts subscription into its own independently-scheduled email — a
 * separate UserState item, not a field on EmailAlertsData, so it can carry its own
 * `alertSchedule` GSI entry (see email-alerts-store.ts) alongside the main one.
 *
 * Deliberately piggybacks on the main emailAlerts subscription rather than being a
 * standalone opt-in: it reuses that subscription's verified email/locale and needs no
 * separate consent flow (no pendingConfirmation state — "active" only exists while the
 * main subscription is also active; the write path enforces this, see subscribeWatchlistDigest
 * in server/alerts/email-alerts-service.ts). Existence of an active record *is* "separate
 * mode" — there is no explicit mode field to keep out of sync with the main subscription.
 */
export const WATCHLIST_DIGEST_ITEM_KEY = "watchlistDigestAlerts" as const;

export const WATCHLIST_DIGEST_STATUSES = ["active", "unsubscribed"] as const;
export type WatchlistDigestStatus = (typeof WATCHLIST_DIGEST_STATUSES)[number];

export interface WatchlistDigestAlertsData extends Record<string, unknown> {
  frequency: AlertFrequency;
  status: WatchlistDigestStatus;
  /** Epoch ms of the last successful watchlist-digest send; this email's own window lower bound. */
  lastSentAt: number | null;
  createdAt: number;
}

export function isAlertFrequency(value: unknown): value is AlertFrequency {
  return typeof value === "string" && (ALL_ALERT_FREQUENCIES as readonly string[]).includes(value);
}

export function isEmailAlertsStatus(value: unknown): value is EmailAlertsStatus {
  return typeof value === "string" && (EMAIL_ALERTS_STATUSES as readonly string[]).includes(value);
}

export function isWatchlistDigestStatus(value: unknown): value is WatchlistDigestStatus {
  return typeof value === "string" && (WATCHLIST_DIGEST_STATUSES as readonly string[]).includes(value);
}

export function savedSearchItemKey(id: string): UserStateSavedSearchKey {
  if (typeof id !== "string" || id.length === 0) {
    throw new Error("[email-alerts] saved search id must be a non-empty string");
  }
  return `${SAVED_SEARCH_ITEM_KEY_PREFIX}${id}`;
}

export function savedSearchFolderItemKey(id: string): UserStateSavedSearchFolderKey {
  if (typeof id !== "string" || id.length === 0) {
    throw new Error("[email-alerts] saved search folder id must be a non-empty string");
  }
  return `${SAVED_SEARCH_FOLDER_ITEM_KEY_PREFIX}${id}`;
}
